/**
 * 12-hour clock rendering for the reservations surfaces (DNKADM-0386 U2) — the ONE
 * formatter behind every "1:00 PM" the Pending list, the reservation drawer, the Now &
 * Next view, the bookings list, My schedule and the slot picker show. Before this
 * module each surface sliced `HH:mm` straight out of the ISO string (or leaned on the
 * device locale), and coaches read `13:00` as a different time zone.
 *
 * **Format only — the zone behaviour of each surface is unchanged.** Two families exist
 * on purpose and are kept apart:
 *  - {@link isoClockAsIs}: the wall clock the ISO-8601 string itself carries (offset
 *    verbatim, never converted — the drawer / pending list / My schedule contract);
 *  - {@link clockInZone}: an instant rendered in an IANA zone (the Now & Next / bookings
 *    list contract), or the device zone when none is given.
 *
 * The 24-hour → 12-hour step is done by hand ({@link clock12}) rather than by
 * `toLocaleTimeString`, so the output is identical everywhere: a plain space before
 * AM/PM (ICU emits U+202F in `en-US`), `12:00 AM` for midnight, and no dependence on
 * the viewer's device locale or 24-hour setting. The calendar grid axis ("1 PM") has
 * its own label and is deliberately not routed here.
 */

/** `"1:05 PM"` from a 24-hour wall clock. Hours wrap mod 24; `0` → `12:xx AM`. */
export function clock12(hour: number, minute: number): string {
  const h = ((Math.trunc(hour) % 24) + 24) % 24;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(Math.trunc(minute)).padStart(2, '0')} ${period}`;
}

/**
 * The wall-clock parts an ISO-8601 string carries AS WRITTEN (`YYYY-MM-DDTHH:mm…`),
 * read off the string with no timezone conversion. Null when it cannot be split.
 */
export function isoWallClock(iso: string | null): { hour: number; minute: number } | null {
  if (!iso) return null;
  const m = /^\d{4}-\d{2}-\d{2}T(\d{2}):(\d{2})/.exec(iso);
  if (!m) return null;
  return { hour: Number(m[1]), minute: Number(m[2]) };
}

/** `"1:00 PM"` for the ISO string's own wall clock (offset verbatim); null when unparseable. */
export function isoClockAsIs(iso: string | null): string | null {
  const parts = isoWallClock(iso);
  return parts ? clock12(parts.hour, parts.minute) : null;
}

/**
 * `"1:00 PM – 2:00 PM"` from two as-written ISO strings; one side alone when the other
 * is missing; `"—"` when neither parses.
 */
export function isoClockRangeAsIs(start: string | null, end: string | null): string {
  const s = isoClockAsIs(start);
  const e = isoClockAsIs(end);
  if (s && e) return `${s} – ${e}`;
  return s ?? e ?? '—';
}

/** Per-zone formatter cache (constructing one is far costlier than formatting with it). */
const zoneFormatters = new Map<string, Intl.DateTimeFormat | null>();

function zoneFormatter(timeZone: string | null): Intl.DateTimeFormat | null {
  const key = timeZone ?? '';
  const cached = zoneFormatters.get(key);
  if (cached !== undefined) return cached;
  let formatter: Intl.DateTimeFormat | null;
  try {
    // `hourCycle: 'h23'` so midnight reads 0 (never 24) before the 12-hour step.
    formatter = new Intl.DateTimeFormat('en-US', {
      hourCycle: 'h23',
      hour: '2-digit',
      minute: '2-digit',
      ...(timeZone ? { timeZone } : {}),
    });
  } catch {
    formatter = null; // an IANA id the runtime rejects — the caller falls back to the device zone
  }
  zoneFormatters.set(key, formatter);
  return formatter;
}

/**
 * `"1:00 PM"` for an instant in `timeZone` (an IANA id), or in the device zone when
 * `timeZone` is null or unusable. `"—"` when the instant does not parse.
 */
export function clockInZone(iso: string | null, timeZone: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  const formatter = zoneFormatter(timeZone) ?? zoneFormatter(null);
  if (!formatter) return '—';
  let hour = -1;
  let minute = -1;
  for (const part of formatter.formatToParts(new Date(t))) {
    if (part.type === 'hour') hour = Number(part.value);
    else if (part.type === 'minute') minute = Number(part.value);
  }
  if (hour < 0 || minute < 0) return '—';
  return clock12(hour, minute);
}

// --- Wall clock → ISO with offset (DNKADM-0390 U2: the manual start time) ------------------------

const pad2 = (n: number) => String(n).padStart(2, '0');

/** The zone's UTC offset in minutes at `utcMs` (DST-correct), read from Intl parts; null when the zone is unusable. */
function zoneOffsetMinutesAt(utcMs: number, timeZone: string): number | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(new Date(utcMs));
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? NaN);
    const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'));
    if (Number.isNaN(asUtc)) return null;
    return Math.round((asUtc - utcMs) / 60000);
  } catch {
    return null;
  }
}

/**
 * `2026-09-16T14:00:00-05:00` for a wall-clock time on a `YYYY-MM-DD` in `timeZone` — the
 * offset the zone has AT that wall time (DST-correct, two-pass). With no usable zone the
 * device's offset is used. Null when the date is not a calendar date.
 */
export function wallClockToIso(date: string, hour: number, minute: number, timeZone: string | null): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return null;
  const p = { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
  let offsetMinutes: number | null = null;
  if (timeZone) {
    const guess = Date.UTC(p.year, p.month - 1, p.day, hour, minute);
    const first = zoneOffsetMinutesAt(guess, timeZone);
    if (first !== null) {
      const second = zoneOffsetMinutesAt(guess - first * 60000, timeZone);
      offsetMinutes = second ?? first;
    }
  }
  if (offsetMinutes === null) {
    const local = new Date(p.year, p.month - 1, p.day, hour, minute);
    if (Number.isNaN(local.getTime())) return null;
    offsetMinutes = -local.getTimezoneOffset();
  }
  const sign = offsetMinutes < 0 ? '-' : '+';
  const abs = Math.abs(offsetMinutes);
  return `${date}T${pad2(hour)}:${pad2(minute)}:00${sign}${pad2(Math.floor(abs / 60))}:${pad2(abs % 60)}`;
}

// --- Calendar-date labels (HOTFIX: drawer header + Week view header) ------------------------------
// The same "one formatter" rule as the clocks above: a `YYYY-MM-DD` is a calendar day the wire
// carries as written, so the label is built from its digits — no `Date` arithmetic in a zone, no
// ICU pattern (ICU renders a day+year-only `toLocaleDateString` as "2026 (day: 20)").

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** The `YYYY-MM-DD` parts as numbers, or null when the value is not a calendar date. */
export function ymdParts(value: string | null): { year: number; month: number; day: number } | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/** `Thu, Sep 17, 2026` for a `YYYY-MM-DD` (the weekday from the calendar day, zone-free); null when unparseable. */
export function dateLabel(value: string | null): string | null {
  const p = ymdParts(value);
  if (!p) return null;
  const weekday = WEEKDAY_SHORT[new Date(Date.UTC(p.year, p.month - 1, p.day, 12)).getUTCDay()];
  return `${weekday}, ${MONTH_SHORT[p.month - 1]} ${p.day}, ${p.year}`;
}

const MONTH_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const WEEKDAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** The weekday index (0 = Sunday) of a calendar day, zone-free. */
function weekdayOf(p: { year: number; month: number; day: number }): number {
  return new Date(Date.UTC(p.year, p.month - 1, p.day, 12)).getUTCDay();
}

/** `Thu` for a `YYYY-MM-DD` (zone-free, DNKADM-0476 F-05); null when unparseable. */
export function weekdayLabel(value: string | null): string | null {
  const p = ymdParts(value);
  return p ? (WEEKDAY_SHORT[weekdayOf(p)] ?? null) : null;
}

/** `Thursday, September 17, 2026` for a `YYYY-MM-DD` (zone-free, DNKADM-0476 F-05); null when unparseable. */
export function longDateLabel(value: string | null): string | null {
  const p = ymdParts(value);
  if (!p) return null;
  return `${WEEKDAY_LONG[weekdayOf(p)]}, ${MONTH_LONG[p.month - 1]} ${p.day}, ${p.year}`;
}

/** `Aug 1` for a `YYYY-MM-DD` or an ISO string's own date (zone-free, no year, DNKADM-0476); null when unparseable. */
export function monthDayLabel(value: string | null): string | null {
  const p = ymdParts(value);
  return p ? `${MONTH_SHORT[p.month - 1]} ${p.day}` : null;
}

/** `September 7, 2026` for a `YYYY-MM-DD` (zone-free, no weekday, DNKADM-0476); null when unparseable. */
export function longMonthDateLabel(value: string | null): string | null {
  const p = ymdParts(value);
  return p ? `${MONTH_LONG[p.month - 1]} ${p.day}, ${p.year}` : null;
}

/** `Oct 1, 2026` for a `YYYY-MM-DD` (zone-free, no weekday); null when unparseable. */
export function shortDateLabel(value: string | null): string | null {
  const p = ymdParts(value);
  if (!p) return null;
  return `${MONTH_SHORT[p.month - 1]} ${p.day}, ${p.year}`;
}

/**
 * `Sep 14 – 20, 2026` for two `YYYY-MM-DD`s in one month; `Sep 28 – Oct 4, 2026` across a month;
 * `Dec 29, 2025 – Jan 4, 2026` across a year. Null when either end is unparseable.
 */
export function dateRangeLabel(start: string | null, end: string | null): string | null {
  const a = ymdParts(start);
  const b = ymdParts(end);
  if (!a || !b) return null;
  if (a.year !== b.year) {
    return `${MONTH_SHORT[a.month - 1]} ${a.day}, ${a.year} – ${MONTH_SHORT[b.month - 1]} ${b.day}, ${b.year}`;
  }
  if (a.month !== b.month) {
    return `${MONTH_SHORT[a.month - 1]} ${a.day} – ${MONTH_SHORT[b.month - 1]} ${b.day}, ${a.year}`;
  }
  return `${MONTH_SHORT[a.month - 1]} ${a.day} – ${b.day}, ${a.year}`;
}
